/**
 * This code was AUTOGENERATED using the Codama library.
 * Please DO NOT EDIT THIS FILE, instead use visitors
 * to add features, then rerun Codama to update it.
 *
 * @see https://github.com/codama-idl/codama
 */

import {
  addDecoderSizePrefix,
  addEncoderSizePrefix,
  assertAccountExists,
  assertAccountsExist,
  combineCodec,
  decodeAccount,
  fetchEncodedAccount,
  fetchEncodedAccounts,
  fixDecoderSize,
  fixEncoderSize,
  getAddressDecoder,
  getAddressEncoder,
  getBooleanDecoder,
  getBooleanEncoder,
  getBytesDecoder,
  getBytesEncoder,
  getI64Decoder,
  getI64Encoder,
  getOptionDecoder,
  getOptionEncoder,
  getStructDecoder,
  getStructEncoder,
  getU32Decoder,
  getU32Encoder,
  getU64Decoder,
  getU64Encoder,
  getU8Decoder,
  getU8Encoder,
  getUtf8Decoder,
  getUtf8Encoder,
  transformEncoder,
  type Account,
  type Address,
  type Codec,
  type Decoder,
  type EncodedAccount,
  type Encoder,
  type FetchAccountConfig,
  type FetchAccountsConfig,
  type MaybeAccount,
  type MaybeEncodedAccount,
  type Option,
  type OptionOrNullable,
  type ReadonlyUint8Array,
} from "@solana/kit";

export const AGENT_REPUTATION_AUTH_DISCRIMINATOR = new Uint8Array([
  30, 249, 165, 44, 155, 194, 188, 175,
]);

export function getAgentReputationAuthDiscriminatorBytes() {
  return fixEncoderSize(getBytesEncoder(), 8).encode(
    AGENT_REPUTATION_AUTH_DISCRIMINATOR,
  );
}

export type AgentReputationAuth = {
  discriminator: ReadonlyUint8Array;
  /** Agent granting authorization */
  agent: Address;
  /** Authorized source (e.g., PayAI facilitator address) */
  authorizedSource: Address;
  /** Maximum number of reputation updates allowed */
  indexLimit: bigint;
  /** Current usage count (number of times used) */
  currentIndex: bigint;
  /** Expiration timestamp (Unix seconds) */
  expiresAt: bigint;
  /**
   * Solana network this authorization is valid on
   * 0 = mainnet-beta, 1 = devnet, 2 = testnet, 3 = localnet
   */
  network: number;
  /** Ed25519 signature proving agent's intent (64 bytes) */
  signature: ReadonlyUint8Array;
  /** Optional nonce for replay protection */
  nonce: Option<string>;
  /** Whether this authorization has been revoked */
  revoked: boolean;
  /** Creation timestamp */
  createdAt: bigint;
  /** Last used timestamp */
  lastUsedAt: Option<bigint>;
  /** Total reputation change applied via this authorization */
  totalReputationChange: bigint;
  /** PDA bump */
  bump: number;
};

export type AgentReputationAuthArgs = {
  /** Agent granting authorization */
  agent: Address;
  /** Authorized source (e.g., PayAI facilitator address) */
  authorizedSource: Address;
  /** Maximum number of reputation updates allowed */
  indexLimit: number | bigint;
  /** Current usage count (number of times used) */
  currentIndex: number | bigint;
  /** Expiration timestamp (Unix seconds) */
  expiresAt: number | bigint;
  /**
   * Solana network this authorization is valid on
   * 0 = mainnet-beta, 1 = devnet, 2 = testnet, 3 = localnet
   */
  network: number;
  /** Ed25519 signature proving agent's intent (64 bytes) */
  signature: ReadonlyUint8Array;
  /** Optional nonce for replay protection */
  nonce: OptionOrNullable<string>;
  /** Whether this authorization has been revoked */
  revoked: boolean;
  /** Creation timestamp */
  createdAt: number | bigint;
  /** Last used timestamp */
  lastUsedAt: OptionOrNullable<number | bigint>;
  /** Total reputation change applied via this authorization */
  totalReputationChange: number | bigint;
  /** PDA bump */
  bump: number;
};

/** Gets the encoder for {@link AgentReputationAuthArgs} account data. */
export function getAgentReputationAuthEncoder(): Encoder<AgentReputationAuthArgs> {
  return transformEncoder(
    getStructEncoder([
      ["discriminator", fixEncoderSize(getBytesEncoder(), 8)],
      ["agent", getAddressEncoder()],
      ["authorizedSource", getAddressEncoder()],
      ["indexLimit", getU64Encoder()],
      ["currentIndex", getU64Encoder()],
      ["expiresAt", getI64Encoder()],
      ["network", getU8Encoder()],
      ["signature", fixEncoderSize(getBytesEncoder(), 64)],
      [
        "nonce",
        getOptionEncoder(
          addEncoderSizePrefix(getUtf8Encoder(), getU32Encoder()),
        ),
      ],
      ["revoked", getBooleanEncoder()],
      ["createdAt", getI64Encoder()],
      ["lastUsedAt", getOptionEncoder(getI64Encoder())],
      ["totalReputationChange", getI64Encoder()],
      ["bump", getU8Encoder()],
    ]),
    (value) => ({
      ...value,
      discriminator: AGENT_REPUTATION_AUTH_DISCRIMINATOR,
    }),
  );
}

/** Gets the decoder for {@link AgentReputationAuth} account data. */
export function getAgentReputationAuthDecoder(): Decoder<AgentReputationAuth> {
  return getStructDecoder([
    ["discriminator", fixDecoderSize(getBytesDecoder(), 8)],
    ["agent", getAddressDecoder()],
    ["authorizedSource", getAddressDecoder()],
    ["indexLimit", getU64Decoder()],
    ["currentIndex", getU64Decoder()],
    ["expiresAt", getI64Decoder()],
    ["network", getU8Decoder()],
    ["signature", fixDecoderSize(getBytesDecoder(), 64)],
    [
      "nonce",
      getOptionDecoder(addDecoderSizePrefix(getUtf8Decoder(), getU32Decoder())),
    ],
    ["revoked", getBooleanDecoder()],
    ["createdAt", getI64Decoder()],
    ["lastUsedAt", getOptionDecoder(getI64Decoder())],
    ["totalReputationChange", getI64Decoder()],
    ["bump", getU8Decoder()],
  ]);
}

/** Gets the codec for {@link AgentReputationAuth} account data. */
export function getAgentReputationAuthCodec(): Codec<
  AgentReputationAuthArgs,
  AgentReputationAuth
> {
  return combineCodec(
    getAgentReputationAuthEncoder(),
    getAgentReputationAuthDecoder(),
  );
}

export function decodeAgentReputationAuth<TAddress extends string = string>(
  encodedAccount: EncodedAccount<TAddress>,
): Account<AgentReputationAuth, TAddress>;
export function decodeAgentReputationAuth<TAddress extends string = string>(
  encodedAccount: MaybeEncodedAccount<TAddress>,
): MaybeAccount<AgentReputationAuth, TAddress>;
export function decodeAgentReputationAuth<TAddress extends string = string>(
  encodedAccount: EncodedAccount<TAddress> | MaybeEncodedAccount<TAddress>,
):
  | Account<AgentReputationAuth, TAddress>
  | MaybeAccount<AgentReputationAuth, TAddress> {
  return decodeAccount(
    encodedAccount as MaybeEncodedAccount<TAddress>,
    getAgentReputationAuthDecoder(),
  );
}

export async function fetchAgentReputationAuth<
  TAddress extends string = string,
>(
  rpc: Parameters<typeof fetchEncodedAccount>[0],
  address: Address<TAddress>,
  config?: FetchAccountConfig,
): Promise<Account<AgentReputationAuth, TAddress>> {
  const maybeAccount = await fetchMaybeAgentReputationAuth(
    rpc,
    address,
    config,
  );
  assertAccountExists(maybeAccount);
  return maybeAccount;
}

export async function fetchMaybeAgentReputationAuth<
  TAddress extends string = string,
>(
  rpc: Parameters<typeof fetchEncodedAccount>[0],
  address: Address<TAddress>,
  config?: FetchAccountConfig,
): Promise<MaybeAccount<AgentReputationAuth, TAddress>> {
  const maybeAccount = await fetchEncodedAccount(rpc, address, config);
  return decodeAgentReputationAuth(maybeAccount);
}

export async function fetchAllAgentReputationAuth(
  rpc: Parameters<typeof fetchEncodedAccounts>[0],
  addresses: Array<Address>,
  config?: FetchAccountsConfig,
): Promise<Account<AgentReputationAuth>[]> {
  const maybeAccounts = await fetchAllMaybeAgentReputationAuth(
    rpc,
    addresses,
    config,
  );
  assertAccountsExist(maybeAccounts);
  return maybeAccounts;
}

export async function fetchAllMaybeAgentReputationAuth(
  rpc: Parameters<typeof fetchEncodedAccounts>[0],
  addresses: Array<Address>,
  config?: FetchAccountsConfig,
): Promise<MaybeAccount<AgentReputationAuth>[]> {
  const maybeAccounts = await fetchEncodedAccounts(rpc, addresses, config);
  return maybeAccounts.map((maybeAccount) =>
    decodeAgentReputationAuth(maybeAccount),
  );
}
